import React from 'react';

import { ReportAiGenerationResultSchema } from '@aline/common';
import type { ReportAiGenerationResult } from '@aline/common';
import { link, routes } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChartColumnBig } from 'lucide-react';
import { useMatch } from 'react-router-dom';
import { useReportMentionEntity } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { isMentionAccessError } from 'src/components/ai/chat/chat-hooks/useMentionEntitiesQuery';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai/ai-thread-id';
import { openLink } from 'src/helpers';
import { useGlobalDrawer } from 'src/layouts';
import { makeStyles } from 'tss-react/mui';

import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  clickableRoot: {
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    cursor: 'pointer',
    transition: theme.transitions.create('border-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  iconBox: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  root: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
  },
  title: {
    fontWeight: 600,
    minWidth: 0,
  },
}));

export const CodeReportGenerationElement = React.memo(function CodeReportGenerationElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(
    codeBlockInfo.code,
    ReportAiGenerationResultSchema,
    'CodeReportGenerationElement',
    { captureErrors: !codeBlockInfo.isNodeStreaming }
  );
  if (!parsed.ok) {
    if (codeBlockInfo.isNodeStreaming) return <ReportGenerationLoadingCard />;

    return <StaticCard detail="Could not display this report." title="Report unavailable" />;
  }

  return <ReportCard result={parsed.data} threadId={codeBlockInfo.threadId} />;
});

function ReportCard({ result, threadId }: { result: ReportAiGenerationResult; threadId: string }) {
  const { classes } = useStyles();
  const { openDrawer } = useGlobalDrawer();
  const isOnChatRoute = Boolean(useMatch({ end: false, path: routes.ChatIndex }));
  const reportResult = useReportMentionEntity(result.reportId);

  if (!reportResult) return <ReportGenerationLoadingCard />;

  if (isMentionAccessError(reportResult))
    return <StaticCard detail="This report is no longer available." title="Report deleted" />;

  const name = reportResult.name || result.name;
  const openReport = () => {
    /**
     * From full-page chat, navigate this tab to the report so the singleton
     * chat docks beside it. Publish the thread and open the drawer first;
     * report pages are a default drawer route and will not auto-open.
     */
    aiThreadIdVar(threadId);
    openDrawer();
    openLink(link.ReportDetail({ reportId: result.reportId }), {
      newTab: !isOnChatRoute,
      title: name,
    });
  };

  return (
    <div className={clsx(classes.root, classes.clickableRoot)} onClick={openReport}>
      <div className={classes.iconBox}>
        <ChartColumnBig size={20} />
      </div>
      <div className={classes.body}>
        <Typography noWrap className={classes.title}>
          {name}
        </Typography>
        <Typography className={classes.meta}>{getReportSummary(result)}</Typography>
      </div>
    </div>
  );
}

function ReportGenerationLoadingCard() {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.iconBox}>
        <ChartColumnBig size={20} />
      </div>
      <div className={classes.body}>
        <Skeleton height={20} variant="text" width={160} />
        <Skeleton height={16} variant="text" width={120} />
      </div>
    </div>
  );
}

function StaticCard({ detail, title }: { detail: string; title: string }) {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.iconBox}>
        <ChartColumnBig size={20} />
      </div>
      <div className={classes.body}>
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.meta}>{detail}</Typography>
      </div>
    </div>
  );
}

function getReportSummary({ columnCount }: ReportAiGenerationResult) {
  return `${columnCount} ${columnCount === 1 ? 'Column' : 'Columns'}`;
}
